/**
 * Scryfall — spec 3b. Shapes shared between the proxy Lambda and the client.
 *
 * The client never calls api.scryfall.com directly: browsers refuse to set a
 * User-Agent header, and Scryfall explicitly blocks generic agents. The API
 * Lambda proxies these two endpoints with a proper identifying UA and caches
 * the answers.
 */

import type { Color } from './types.js';

export interface ScryfallSuggestion {
  name: string;
}

/** Only the fields we actually read off the card object. */
export interface ScryfallCard {
  id: string;
  name: string;
  color_identity: Color[];
  /** Never construct these by hand — Scryfall has moved hosting domains before. */
  art_crop: string | null;
  /** The whole card, front face, big enough to read the rules text off. */
  image: string | null;
  type_line: string | null;
  set_name: string | null;
  /** Both faces joined, for a card that has two. */
  oracle_text: string | null;
  mana_cost: string | null;
  /** Scryfall's own list. "Partner" appears here, which is most of the work. */
  keywords: string[];
}

/**
 * Whether this commander is allowed a second one.
 *
 * Worth checking rather than always offering, because the overwhelming majority
 * of commanders cannot pair and an "add a partner" button on Alela is an
 * invitation to record something that is not legal.
 *
 * Five ways a deck ends up with two commanders, and they are all in the text:
 * Partner, Partner with (a named one, which still needs the field), Choose a
 * Background, Friends forever, and the Doctor's companion pairing. Backgrounds
 * and Doctors are the other half of two of those, so they count as well — a
 * deck can be registered from either end.
 *
 * Read from the card rather than kept as a list of names. Wizards has added a
 * new pairing mechanic roughly every other year and a hardcoded list would be
 * wrong the first time one of them showed up at the table.
 */
const PAIRING = /\bpartner\b|choose a background|friends forever|doctor's companion/i;

export function canPair(card: Pick<ScryfallCard, 'oracle_text' | 'keywords' | 'type_line'>): boolean {
  if (card.keywords?.some((k) => PAIRING.test(k))) return true;
  if (card.oracle_text && PAIRING.test(card.oracle_text)) return true;
  // The other half of a pairing: a Background is chosen *by* a commander, and a
  // Doctor is companioned by one, so neither says anything about pairing itself.
  return /\bBackground\b|\bTime Lord Doctor\b/i.test(card.type_line ?? '');
}

export interface CommanderPick {
  scryfallId: string;
  name: string;
  colors: Color[];
  artCropUrl: string | null;
  /**
   * Whether this one is allowed a second commander.
   *
   * Answered here rather than left for the caller to work out, so the question
   * is settled once at the moment the card is in hand. Everything downstream
   * gets a boolean and nobody has to keep the rules for five pairing mechanics
   * in two places.
   */
  pairs: boolean;
}

export function cardToCommanderPick(card: ScryfallCard): CommanderPick {
  return {
    scryfallId: card.id,
    name: card.name,
    colors: card.color_identity ?? [],
    artCropUrl: card.art_crop,
    pairs: canPair(card),
  };
}

/** Spec 3b — debounce the autocomplete input by ~250ms; the limit is ~10 req/s. */
export const AUTOCOMPLETE_DEBOUNCE_MS = 250;
export const SCRYFALL_CACHE_SECONDS = 24 * 60 * 60;
